import { FC, SVGProps } from 'react';

export const DEFAULT_PAGE_SIZE = 8;

/**
 * Состояние видео в Cloudflare Stream, пока оно pending: pendingupload — байты ещё не пришли
 * полностью; downloading, queued, inprogress — Stream обрабатывает файл; missing — видео в Stream
 * нет (загрузка просрочена и удалена)
 */
export type StreamVideoState = 'pendingupload' | 'downloading' | 'queued' | 'inprogress' | 'missing';

/**
 * Незавершённая загрузка видео (пока pending, из деталей урока у админа): адрес tus-загрузки и
 * отпечаток файла (имя — в `name` видео). Тот же файл докачивается по адресу с места обрыва
 */
export interface IStreamVideoResumableUpload {
  url: string;
  fileSize: number | null;
  /** File.lastModified, мс; null — при выдаче адреса его не передали */
  fileLastModified: number | null;
}

export interface IStreamVideo extends IFile {
  provider: 'cloudflare-stream';
  status: 'pending' | 'ready' | 'error';
  thumbnailUrl?: string;
  embedUrl?: string;
  duration?: number;
  /** почему Stream отклонил файл; есть только при status: 'error' */
  errorReason?: string | null;
  /** когда url/embedUrl/thumbnailUrl перестанут работать; null — ссылки бессрочные */
  urlExpiresAt?: string | null;
  /** пока pending: состояние видео в Stream по данным деталей урока; null — неизвестно */
  streamState?: StreamVideoState | null;
  /** пока Stream обрабатывает видео: прогресс обработки в процентах */
  pctComplete?: number | null;
  /** пока pending: до какого времени Stream принимает байты по выданному адресу */
  uploadExpiresAt?: string | null;
  /** пока pending, только у админа: как продолжить прерванную загрузку тем же файлом */
  resumableUpload?: IStreamVideoResumableUpload | null;
}

export type StaticImageData = {
  src: string;
  height: number;
  width: number;
  blurDataURL?: string;
};

export interface IFile {
  id: number;
  name: string;
  url: string;
  size: number;
  mime: string;
}

export interface FormSubscribeRequest {
  phoneNumber: string;
  firstName: string;
  lastName: string;
  email: string;
  comment: string;
  subscription?: string;
}

export interface FormQuestionnaireRequest {
  phoneNumber: string;
  email: string;
  niche: string;
  fullName: string;
  telegramNickname: string;
  partnerOrSpeaker: string;
}

export interface ApiResponse {
  success: boolean;
}

export interface SuccessApiResponse<Data> extends ApiResponse {
  data: Data;
  success: boolean;
}

export interface RejectApiResponse extends ApiResponse {
  message: string;
}

export interface ErrorApiResponse<
  Error extends object | undefined = undefined,
> extends ApiResponse {
  message: string;
  errors?: Error;
}

export type TablePaginationType = {
  page: number;
  pageSize: number;
  total: number;
  pageCount: number;
};

export type SvgType = FC<SVGProps<SVGSVGElement>> | string;

export type ImageType = string | StaticImageData | null;

export type AnyFunction = (...args: any[]) => any;

export type ObjectType = Record<string, any>;

export interface SeoData {
  [key: string]: ObjectType;
}

export type Primitive = string | number | boolean | null;

export type Unit = 'px' | '%' | 'rem' | 'em';

export type ResponsiveSize =
  | `${number}${Unit}`
  | number
  | {
      desktop: `${number}${Unit}` | number;
      mobile: `${number}${Unit}` | number;
    };

export type ResponsiveImage = {
  mobile?: ImageType;
  tablet?: ImageType;
  desktop: ImageType;
};

export type Social = {
  url: string;
  icon: SvgType;
};

export type Speaker = {
  id: number;
  name: string;
  description: string;
  image: StaticImageData;
  url: string;
};

export type PartnerItem = {
  id: number;
  icon: SvgType;
};

export type MenuItem = {
  id: number;
  label: string;
  href?: string;
  icon?: any;
  children?: MenuItem[];
};

export type SkillItemType = {
  id: number;
  title: string;
  description: string;
  tags: string[];
};

export type SortOptions = {
  id: string;
  title: string;
  options: {
    label: string;
    value: string;
  }[];
};

export type SocialType = {
  id: number;
  name: string;
  link: string;
  iconUrl?: string;
};
